import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Pill, Check, Loader2, Edit3, ChevronDown, ChevronRight,
  Clock, User, X, AlertCircle, Eye, Send
} from 'lucide-react';

function RefillBucketView({ onComplete }) {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [prescribing, setPrescribing] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('hfc_refill_view') || 'grid');
  const [editingMed, setEditingMed] = useState(null);
  const [patientPharmacies, setPatientPharmacies] = useState({}); // { patientId: [...pharmacies] }
  const [bulkPharmPicker, setBulkPharmPicker] = useState(null); // patientId or null

  const fetchRefills = useCallback(() => {
    api.get('/my-day/refills/by-patient').then(r => {
      setPatients(r.data.patients || []);
      const sel = {};
      (r.data.patients || []).forEach(p => p.medications.forEach(m => { sel[m.id] = true; }));
      setSelected(sel);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchRefills(); }, [fetchRefills]);

  // Load pharmacies for each patient
  useEffect(() => {
    patients.forEach(async (group) => {
      if (!patientPharmacies[group.patient_id]) {
        try {
          const res = await api.get(`/patients/${group.patient_id}/pharmacies`);
          setPatientPharmacies(prev => ({ ...prev, [group.patient_id]: res.data.pharmacies || [] }));
        } catch {}
      }
    });
  }, [patients]); // eslint-disable-line

  const handleBulkPharmacy = async (patientId, meds, pharmacyName) => {
    const ids = meds.map(m => m.id);
    try {
      await api.post('/my-day/refills/bulk-set-pharmacy', { item_ids: ids, pharmacy_name: pharmacyName });
      toast.success(`${pharmacyName} set for all ${ids.length} meds`);
      setBulkPharmPicker(null);
      fetchRefills();
    } catch { toast.error('Failed'); }
  };

  const toggleView = (mode) => { setViewMode(mode); localStorage.setItem('hfc_refill_view', mode); };
  const toggleMed = (id) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  const selectAllForPatient = (meds) => setSelected(prev => { const n = { ...prev }; meds.forEach(m => { n[m.id] = true; }); return n; });
  const deselectAllForPatient = (meds) => setSelected(prev => { const n = { ...prev }; meds.forEach(m => { n[m.id] = false; }); return n; });

  const handlePrescribe = async (patientMeds) => {
    const ids = patientMeds.filter(m => selected[m.id]).map(m => m.id);
    if (ids.length === 0) return;
    setPrescribing(true);
    try {
      const res = await api.post('/my-day/refills/batch-prescribe', { item_ids: ids });
      if (res.data.filed > 0) toast.success(`${res.data.filed} prescription(s) sent`);
      if (res.data.errors > 0) toast.error(`${res.data.errors} item(s) had issues`);
      onComplete();
    } catch { toast.error('Prescribe failed'); }
    finally { setPrescribing(false); }
  };

  const handleSaveEdit = async (itemId, updates) => {
    try {
      await api.put(`/my-day/items/${itemId}/edit-details`, updates);
      toast.success('Updated');
      setEditingMed(null);
      fetchRefills();
    } catch { toast.error('Save failed'); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

  if (patients.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="empty-refills">
        <div className="p-4 rounded-2xl bg-muted/50 mb-4"><Pill className="w-8 h-8 text-muted-foreground" /></div>
        <p className="text-sm font-medium text-foreground mb-1">No pending prescriptions</p>
        <p className="text-xs text-muted-foreground">Use "+ New Order" to create refills</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-1" data-testid="refill-bucket-view">
      {/* View Toggle (7H.2) */}
      <div className="flex justify-end gap-1" data-testid="view-mode-toggle">
        {[{ key: 'grid', label: 'Grid' }, { key: 'list', label: 'List' }].map(v => (
          <button key={v.key} onClick={() => toggleView(v.key)}
            className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors ${viewMode === v.key ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
            data-testid={`view-mode-${v.key}`}>
            {v.label}
          </button>
        ))}
      </div>

      {patients.map(group => {
        const meds = group.medications;
        const selectedCount = meds.filter(m => selected[m.id]).length;

        return (
          <div key={group.patient_id} className="rounded-xl border bg-card overflow-hidden" data-testid={`refill-patient-${group.patient_id}`}>
            {/* Patient header */}
            <div className="px-4 py-3 border-b bg-muted/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-semibold text-primary">
                  {group.patient_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <span className="text-sm font-semibold">{group.patient_name}</span>
                <Badge variant="outline" className="text-[10px]">{meds.length} med{meds.length > 1 ? 's' : ''}</Badge>
              </div>
              <div className="flex gap-1.5 items-center">
                {/* Bulk pharmacy selector */}
                <div className="relative">
                  <button onClick={() => setBulkPharmPicker(bulkPharmPicker === group.patient_id ? null : group.patient_id)}
                    className="text-[10px] px-2 py-1 rounded font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                    data-testid={`bulk-pharmacy-${group.patient_id}`}>
                    Pharmacy
                  </button>
                  {bulkPharmPicker === group.patient_id && (
                    <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-lg border shadow-lg z-20 py-1">
                      <p className="px-3 py-1 text-[9px] text-slate-400 uppercase font-semibold">Set pharmacy for all {meds.length} meds</p>
                      {(patientPharmacies[group.patient_id] || []).slice(0, 4).map(ph => (
                        <button key={ph.id || ph.pharmacy_name} onClick={() => handleBulkPharmacy(group.patient_id, meds, ph.pharmacy_name || ph.name)}
                          className="w-full text-left px-3 py-2 text-[10px] hover:bg-blue-50 flex items-center gap-1.5">
                          <span>{ph.pharmacy_name || ph.name}</span>
                          <span className="text-[8px] text-slate-300 ml-auto">{ph.use_count || 1}x</span>
                        </button>
                      ))}
                      {(!patientPharmacies[group.patient_id] || patientPharmacies[group.patient_id].length === 0) &&
                        <p className="text-[9px] text-slate-400 px-3 py-2">No recent pharmacies</p>}
                    </div>
                  )}
                </div>
                <button onClick={() => selectAllForPatient(meds)} className="text-[10px] px-2 py-1 rounded font-medium bg-muted text-muted-foreground hover:text-foreground"
                  data-testid={`select-all-${group.patient_id}`}>Select All</button>
                <button onClick={() => deselectAllForPatient(meds)} className="text-[10px] px-2 py-1 rounded bg-muted text-muted-foreground hover:text-foreground"
                  data-testid={`deselect-all-${group.patient_id}`}>Deselect All</button>
              </div>
            </div>

            {/* Medication cards — grid or list */}
            <div className={`p-3 ${viewMode === 'grid' ? 'grid gap-2 sm:grid-cols-2' : 'space-y-1'}`}>
              {/* List mode column headers */}
              {viewMode === 'list' && meds.length > 0 && (
                <div className="flex items-center gap-0 h-6 text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">
                  <div className="w-8 shrink-0" />
                  <div className="w-[22%] px-1">Medication</div>
                  <div className="w-[13%] px-1">Dose</div>
                  <div className="w-[18%] px-1">Sig</div>
                  <div className="w-[7%] px-1 text-center">Qty</div>
                  <div className="w-[7%] px-1 text-center">Refills</div>
                  <div className="w-[15%] px-1">Pharmacy</div>
                  <div className="w-[10%] px-1">Dx</div>
                  <div className="w-7 shrink-0" />
                </div>
              )}
              {meds.map(med => (
                <MedCard key={med.id} med={med} isSelected={selected[med.id]}
                  viewMode={viewMode}
                  onToggle={() => toggleMed(med.id)}
                  onEdit={() => setEditingMed(med)}
                />
              ))}
            </div>

            {/* Prescribe action */}
            <div className="px-4 py-2.5 border-t bg-muted/10 flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground">{selectedCount} of {meds.length} selected</span>
              <Button size="sm" className="gap-1.5" onClick={() => handlePrescribe(meds)}
                disabled={selectedCount === 0 || prescribing} data-testid={`prescribe-btn-${group.patient_id}`}>
                {prescribing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Prescribe Selected ({selectedCount})
              </Button>
            </div>
          </div>
        );
      })}

      {/* Edit Drawer (7H.2) */}
      {editingMed && (
        <MedEditDrawer med={editingMed} onSave={handleSaveEdit} onClose={() => setEditingMed(null)} />
      )}
    </div>
  );
}


/* ═══════════════════════════════════════
   MEDICATION CARD — Selectable + Editable (7H.2)
   ═══════════════════════════════════════ */


function MedCard({ med, isSelected, viewMode, onToggle, onEdit }) {
  const hasLinkage = !!med.linked_diagnosis;
  const lastClickRef = React.useRef(0);
  const details = med.edit_details || {};
  const bodyLines = (med.body || '').split('\n').filter(Boolean);

  const medName = details.medication_name || med.title?.split(/\s\d/)[0] || med.title || '';
  const dose = details.dose || _extractField(bodyLines, 'Dose:') || '';
  const sig = details.sig || _extractField(bodyLines, 'Sig:') || '';
  const qty = details.quantity || _extractField(bodyLines, 'Qty:') || '';
  const refills = details.refills || _extractField(bodyLines, 'Refills:') || '';
  const pharmacy = med.pharmacy_name || details.pharmacy || _extractField(bodyLines, 'Pharmacy:') || '';

  const handleClick = () => {
    const now = Date.now();
    if (now - lastClickRef.current < 350) { onEdit(); }
    else { onToggle(); }
    lastClickRef.current = now;
  };

  // LIST MODE: clean read-only spreadsheet row
  if (viewMode === 'list') {
    return (
      <div onClick={handleClick}
        className={`relative flex items-center gap-0 rounded-md border-2 transition-all duration-150 cursor-pointer group h-10
          ${isSelected ? 'border-emerald-400 bg-emerald-50/30' : 'border-red-300 bg-red-50/10 hover:border-red-400'}
          ${!hasLinkage ? 'ring-1 ring-amber-200' : ''}`}
        data-testid={`med-card-${med.id}`}>
        <div className={`w-8 flex items-center justify-center shrink-0 ${isSelected ? 'text-emerald-500' : 'text-red-300'}`}
          onClick={e => { e.stopPropagation(); onToggle(); }}>
          {isSelected ? <Check className="w-3.5 h-3.5" /> : <span className="w-3 h-3 rounded-full border-2 border-current" />}
        </div>
        <div className="w-[22%] min-w-0 px-1"><span className="text-xs font-semibold text-foreground truncate block">{medName}</span></div>
        <div className="w-[13%] min-w-0 px-1"><span className="text-[11px] text-muted-foreground truncate block">{dose || '\u2014'}</span></div>
        <div className="w-[18%] min-w-0 px-1"><span className="text-[11px] text-muted-foreground truncate block">{sig || '\u2014'}</span></div>
        <div className="w-[7%] min-w-0 px-1 text-center"><span className="text-[11px] text-muted-foreground tabular-nums">{qty || '\u2014'}</span></div>
        <div className="w-[7%] min-w-0 px-1 text-center"><span className="text-[11px] text-muted-foreground tabular-nums">{refills || '\u2014'}</span></div>
        <div className="w-[15%] min-w-0 px-1"><span className="text-[11px] text-muted-foreground truncate block">{pharmacy || '\u2014'}</span></div>
        <div className="w-[10%] min-w-0 px-1 flex items-center gap-1">
          {hasLinkage ? (
            <><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" /><span className="text-[9px] text-emerald-600 font-mono truncate">{med.linked_diagnosis}</span></>
          ) : (
            <><span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse shrink-0" /><span className="text-[9px] text-amber-600">No Dx</span></>
          )}
        </div>
        <button onClick={(e) => { e.stopPropagation(); onEdit(); }}
          className="w-7 flex items-center justify-center shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`edit-btn-${med.id}`}><Edit3 className="w-3 h-3 text-muted-foreground" /></button>
      </div>
    );
  }

  // GRID MODE
  return (
    <div onClick={handleClick}
      className={`relative text-left rounded-lg p-3 border-2 transition-all duration-150 cursor-pointer group
        ${isSelected ? 'border-emerald-400 bg-emerald-50/40 shadow-sm' : 'border-red-300 bg-red-50/20 hover:border-red-400'}
        ${!hasLinkage ? 'ring-2 ring-amber-200' : ''}`}
      data-testid={`med-card-${med.id}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-semibold text-foreground">{med.title}</span>
        {isSelected && <Check className="w-4 h-4 text-emerald-500 shrink-0" />}
      </div>
      {bodyLines.slice(1).map((line, i) => (
        <p key={i} className="text-[11px] text-muted-foreground mt-0.5">{line}</p>
      ))}
      {pharmacy && <p className="text-[10px] text-blue-500 mt-1">Rx \u2192 {pharmacy}</p>}
      <div className="mt-1.5 flex items-center gap-1">
        {hasLinkage ? (
          <><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /><span className="text-[9px] text-emerald-600 font-mono">{med.linked_diagnosis}</span></>
        ) : (
          <><span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" /><span className="text-[9px] text-amber-600">Missing Dx</span></>
        )}
      </div>
      <button onClick={(e) => { e.stopPropagation(); onEdit(); }}
        className="absolute top-2 right-2 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-accent transition-all"
        data-testid={`edit-btn-${med.id}`}><Edit3 className="w-3 h-3 text-muted-foreground" /></button>
    </div>
  );
}



/* ═══════════════════════════════════════
   MEDICATION EDIT DRAWER (7H.2)
   ═══════════════════════════════════════ */


function MedEditDrawer({ med, onSave, onClose }) {
  const details = med.edit_details || {};
  const bodyLines = (med.body || '').split('\n');
  const medName = details.medication_name || med.title?.split(' ')[0] || '';
  const [form, setForm] = useState({
    medication_name: medName,
    dose: details.dose || _extractField(bodyLines, 'Dose:') || '',
    units: details.units || '',
    sig: details.sig || _extractField(bodyLines, 'Sig:') || '',
    quantity: details.quantity || _extractField(bodyLines, 'Qty:') || '',
    refills: details.refills || _extractField(bodyLines, 'Refills:') || '',
    pharmacy: details.pharmacy || med.pharmacy_name || _extractField(bodyLines, 'Pharmacy:') || '',
    linked_diagnosis: med.linked_diagnosis || '',
  });
  const [saving, setSaving] = useState(false);
  const [medDefaults, setMedDefaults] = useState(null);
  const [customFields, setCustomFields] = useState({}); // { dose: true, sig: true, etc. }

  // Fetch smart defaults from medication database
  useEffect(() => {
    if (!medName) return;
    api.get(`/medications/${encodeURIComponent(medName)}/defaults`).then(r => {
      const d = r.data.defaults;
      if (d) {
        setMedDefaults(d);
        setForm(prev => ({
          ...prev,
          dose: prev.dose || d.default_dose || d.strengths?.[0] || '',
          sig: prev.sig || d.default_sig || '',
          quantity: prev.quantity || (d.default_quantity ? String(d.default_quantity) : '30'),
          refills: prev.refills || (d.default_refills !== undefined ? String(d.default_refills) : ''),
        }));
      }
    }).catch(() => {});
  }, [medName]);

  const handleSave = async () => {
    setSaving(true);
    await onSave(med.id, form);
    setSaving(false);
  };

  const strengths = medDefaults?.strengths || [];
  const commonSigs = [
    medDefaults?.default_sig,
    '1 tab PO daily', '1 tab PO BID', '1 tab PO TID',
    '1 cap PO daily', '1 tab PO QHS', 'As directed',
  ].filter((s, i, arr) => s && arr.indexOf(s) === i);
  const commonQtys = ['15', '20', '28', '30', '60', '90', '120'];
  const commonRefills = ['0', '1', '2', '3', '4', '5', '6', '11'];

  const SmartField = ({ label, fieldKey, options, value, testId }) => {
    const isCustom = customFields[fieldKey];
    return (
      <div>
        <div className="flex items-center justify-between">
          <label className="text-[9px] font-medium text-muted-foreground uppercase tracking-wider">{label}</label>
          <button onClick={() => setCustomFields(prev => ({ ...prev, [fieldKey]: !prev[fieldKey] }))}
            className="text-[8px] text-blue-500 hover:text-blue-700">{isCustom ? 'use list' : 'custom'}</button>
        </div>
        {isCustom ? (
          <input type="text" value={value} onChange={e => setForm(prev => ({ ...prev, [fieldKey]: e.target.value }))}
            placeholder={`Custom ${label.toLowerCase()}...`}
            className="w-full mt-0.5 text-xs border rounded px-2 py-1.5 bg-background" data-testid={testId} />
        ) : (
          <select value={options.includes(value) ? value : '__other'} onChange={e => {
            if (e.target.value === '__other') { setCustomFields(prev => ({ ...prev, [fieldKey]: true })); }
            else { setForm(prev => ({ ...prev, [fieldKey]: e.target.value })); }
          }} className="w-full mt-0.5 text-xs border rounded px-2 py-1.5 bg-background" data-testid={testId}>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
            {!options.includes(value) && value && <option value={value}>{value}</option>}
            <option value="__other">Custom...</option>
          </select>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-y-0 right-0 w-80 bg-card border-l shadow-xl z-50 flex flex-col animate-in slide-in-from-right duration-200"
      data-testid="med-edit-drawer">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <h3 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Edit Prescription</h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-accent" data-testid="edit-drawer-close">
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {/* Medication name — read-only */}
        <div>
          <label className="text-[9px] font-medium text-muted-foreground uppercase tracking-wider">Medication</label>
          <div className="mt-0.5 text-sm font-semibold text-slate-700 px-2 py-1.5 bg-slate-50 rounded border">{form.medication_name}</div>
        </div>

        {/* Dose + Form */}
        <div className="grid grid-cols-2 gap-2">
          <SmartField label="Dose" fieldKey="dose" options={strengths.length > 0 ? strengths : ['5mg','10mg','20mg','25mg','40mg','50mg','100mg']} value={form.dose} testId="edit-field-dose" />
          <div>
            <label className="text-[9px] font-medium text-muted-foreground uppercase tracking-wider">Form</label>
            <div className="mt-0.5 text-xs text-slate-500 px-2 py-1.5 bg-slate-50 rounded border">
              {medDefaults?.form || 'Tablet'}
            </div>
          </div>
        </div>

        {/* Sig */}
        <SmartField label="Sig" fieldKey="sig" options={commonSigs} value={form.sig} testId="edit-field-sig" />

        {/* Quantity + Refills */}
        <div className="grid grid-cols-2 gap-2">
          <SmartField label="Quantity" fieldKey="quantity" options={commonQtys} value={form.quantity} testId="edit-field-quantity" />
          <SmartField label="Refills" fieldKey="refills" options={commonRefills} value={form.refills} testId="edit-field-refills" />
        </div>

        {/* Pharmacy */}
        <div>
          <label className="text-[9px] font-medium text-muted-foreground uppercase tracking-wider">Pharmacy</label>
          <input type="text" value={form.pharmacy} onChange={e => setForm(prev => ({ ...prev, pharmacy: e.target.value }))}
            placeholder="CVS Main St" className="w-full mt-0.5 text-xs border rounded px-2 py-1.5 bg-background" data-testid="edit-field-pharmacy" />
        </div>

        {/* Diagnosis */}
        <div>
          <label className="text-[9px] font-medium text-muted-foreground uppercase tracking-wider">Diagnosis (ICD-10)</label>
          <input type="text" value={form.linked_diagnosis} onChange={e => setForm(prev => ({ ...prev, linked_diagnosis: e.target.value }))}
            placeholder="e.g., I10, E11.9" className="w-full mt-0.5 text-xs border rounded px-2 py-1.5 bg-background font-mono" data-testid="edit-field-linked_diagnosis" />
        </div>

        {!form.linked_diagnosis && (
          <p className="text-[10px] text-amber-600 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> Diagnosis required before prescribing
          </p>
        )}
      </div>
      <div className="px-4 py-3 border-t flex gap-2">
        <Button size="sm" className="flex-1 gap-1" onClick={handleSave} disabled={saving} data-testid="edit-save-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Save
        </Button>
        <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  );
}


function _extractField(lines, prefix) {
  const line = lines.find(l => l.startsWith(prefix));
  return line ? line.replace(prefix, '').trim() : '';
}




/* ═══════════════════════════════════════
   MESSAGES BUCKET — Conversation View (7I)
   ═══════════════════════════════════════ */



export { RefillBucketView, MedCard, MedEditDrawer };
